import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Button,
} from 'antd';
import {
  EnvironmentOutlined,
  TeamOutlined,
  SoundOutlined,
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#eb2f96', '#13c2c2', '#722ed1', '#f5222d', '#2f54eb'];
const COLORS_INGRESOS = ['#52c41a', '#1890ff', '#faad14', '#eb2f96', '#13c2c2', '#722ed1', '#2f54eb', '#f5222d'];
const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const DashboardClub = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    canchas: 0,
    jugadores: 0,
    difusiones: 0,
    reservasHoy: 0,
    reservasSemana: 0,
    reservasMes: 0,
    ingresosHoy: 0,
    ingresosSemana: 0,
    ingresosMes: 0,
  });
  const [proximasReservas, setProximasReservas] = useState([]);
  const [reservasDelMes, setReservasDelMes] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const hoy = dayjs().format('YYYY-MM-DD');
      const inicioMes = dayjs().startOf('month').format('YYYY-MM-DD');
      const finMes = dayjs().endOf('month').format('YYYY-MM-DD');
      const inicioSemana = dayjs().startOf('week').format('YYYY-MM-DD');
      const finSemana = dayjs().endOf('week').format('YYYY-MM-DD');

      const [resCanchas, resJugadores, resDifusiones, resReservas] = await Promise.all([
        apiClient.get('/club/canchas/activas'),
        apiClient.get('/club/jugadores'),
        apiClient.get('/club/difusiones'),
        apiClient.get(`/club/reservas?fechaDesde=${inicioMes}&fechaHasta=${finMes}`),
      ]);

      const canchas = resCanchas.ok ? await resCanchas.json() : [];
      const jugadores = resJugadores.ok ? await resJugadores.json() : [];
      const difusiones = resDifusiones.ok ? await resDifusiones.json() : [];
      const reservasMes = resReservas.ok ? await resReservas.json() : [];

      const reservasArray = Array.isArray(reservasMes) ? reservasMes : [];
      const hoyStr = hoy;
      const reservasHoy = reservasArray.filter((r) => (r.tcl_fecha || '').toString().substring(0, 10) === hoyStr);
      const reservasSemana = reservasArray.filter((r) => {
        const f = (r.tcl_fecha || '').toString().substring(0, 10);
        return f >= inicioSemana && f <= finSemana;
      });

      const sumIngresos = (list) =>
        list.reduce((acc, r) => acc + (Number(r.rcl_precio_pagado) || 0), 0);

      setStats({
        canchas: canchas.length,
        jugadores: jugadores.length,
        difusiones: difusiones.length,
        reservasHoy: reservasHoy.length,
        reservasSemana: reservasSemana.length,
        reservasMes: reservasArray.length,
        ingresosHoy: sumIngresos(reservasHoy),
        ingresosSemana: sumIngresos(reservasSemana),
        ingresosMes: sumIngresos(reservasArray),
      });

      // Próximas reservas: fecha >= hoy, ordenar por fecha y hora, limitar a 10
      const hoyDate = dayjs().format('YYYY-MM-DD');
      const proximas = reservasArray
        .filter((r) => (r.tcl_fecha || '').toString().substring(0, 10) >= hoyDate)
        .sort((a, b) => {
          const fa = (a.tcl_fecha || '').toString().substring(0, 10);
          const fb = (b.tcl_fecha || '').toString().substring(0, 10);
          if (fa !== fb) return fa.localeCompare(fb);
          return (a.tcl_hora_desde || '').localeCompare(b.tcl_hora_desde || '');
        })
        .slice(0, 10);
      setProximasReservas(proximas);
      setReservasDelMes(reservasArray);
    } catch (error) {
      console.error('Error al cargar dashboard club:', error);
      message.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Datos para gráficos (torta y barras)
  const { reservasPorCancha, ingresosPorCancha, reservasPorDiaSemana } = useMemo(() => {
    const list = Array.isArray(reservasDelMes) ? reservasDelMes : [];
    const porCancha = {};
    const ingresosCancha = {};
    const porDia = Array.from({ length: 7 }, (_, i) => ({ dia: DIAS_SEMANA[i], reservas: 0, ingresos: 0 }));

    list.forEach((r) => {
      const cancha = r.cancha_nombre || 'Sin cancha';
      porCancha[cancha] = (porCancha[cancha] || 0) + 1;
      ingresosCancha[cancha] = (ingresosCancha[cancha] || 0) + (Number(r.rcl_precio_pagado) || 0);
      const fecha = (r.tcl_fecha || '').toString().substring(0, 10);
      if (fecha) {
        const d = dayjs(fecha).day();
        porDia[d].reservas += 1;
        porDia[d].ingresos += Number(r.rcl_precio_pagado) || 0;
      }
    });

    return {
      reservasPorCancha: Object.entries(porCancha).map(([name, value]) => ({ name, value })),
      ingresosPorCancha: Object.entries(ingresosCancha)
        .map(([name, value]) => ({ name, value: Math.round(value) }))
        .filter((d) => d.value > 0),
      reservasPorDiaSemana: porDia,
    };
  }, [reservasDelMes]);

  const formatMoneda = (n) => `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const columnasProximas = [
    {
      title: 'Fecha',
      dataIndex: 'tcl_fecha',
      key: 'tcl_fecha',
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
      width: 100,
    },
    {
      title: 'Horario',
      key: 'horario',
      render: (_, r) => `${(r.tcl_hora_desde || '').toString().substring(0, 5)} - ${(r.tcl_hora_hasta || '').toString().substring(0, 5)}`,
      width: 110,
    },
    {
      title: 'Cancha',
      dataIndex: 'cancha_nombre',
      key: 'cancha_nombre',
      render: (t) => t || '-',
    },
    {
      title: 'Cliente',
      dataIndex: 'rcl_nombre_cliente',
      key: 'rcl_nombre_cliente',
      render: (t) => t || '-',
    },
    {
      title: 'Ingreso',
      dataIndex: 'rcl_precio_pagado',
      key: 'rcl_precio_pagado',
      render: (v) => formatMoneda(v),
      width: 100,
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <Spin size="large" tip="Cargando dashboard..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? 16 : 24 }}>
        <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#ffffff' }}>
            Dashboard - Gestión del Club
          </Title>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Actualizar
          </Button>
        </div>

        {/* Resumen general */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Canchas activas"
                value={stats.canchas}
                prefix={<EnvironmentOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Jugadores registrados"
                value={stats.jugadores}
                prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Novedades (difusiones)"
                value={stats.difusiones}
                prefix={<SoundOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Reservas por cancha (este mes)"
              style={{ background: '#2d2d2d', borderColor: '#404040', height: '100%' }}
            >
              {reservasPorCancha.length === 0 ? (
                <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">Sin reservas este mes</Text>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={reservasPorCancha}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#8c8c8c' }}
                    >
                      {reservasPorCancha.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => [`${value} reservas`, 'Cantidad']}
                      labelFormatter={(label) => label}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos por cancha (este mes)"
              style={{ background: '#2d2d2d', borderColor: '#404040', height: '100%' }}
            >
              {ingresosPorCancha.length === 0 ? (
                <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">Sin ingresos este mes</Text>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={ingresosPorCancha}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#8c8c8c' }}
                    >
                      {ingresosPorCancha.map((_, i) => (
                        <Cell key={i} fill={COLORS_INGRESOS[i % COLORS_INGRESOS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => [formatMoneda(value), 'Ingresos']}
                      labelFormatter={(label) => label}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title="Reservas e ingresos por día de la semana (este mes)"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={reservasPorDiaSemana}
                  margin={{ top: 16, right: 24, left: 24, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                  <XAxis dataKey="dia" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                  <YAxis yAxisId="left" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                    labelStyle={{ color: '#e8e8e8' }}
                    formatter={(value, name) => [
                      name === 'reservas' ? value : formatMoneda(value),
                      name === 'reservas' ? 'Reservas' : 'Ingresos',
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Legend wrapperStyle={{ color: '#b0b0b0' }} />
                  <Bar yAxisId="left" dataKey="reservas" name="Reservas" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="ingresos" name="Ingresos ($)" fill="#52c41a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Reservas e ingresos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Reservas hoy"
                value={stats.reservasHoy}
                prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Reservas esta semana"
                value={stats.reservasSemana}
                prefix={<CalendarOutlined style={{ color: '#13c2c2' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Reservas este mes"
                value={stats.reservasMes}
                prefix={<CalendarOutlined style={{ color: '#eb2f96' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos hoy"
                value={stats.ingresosHoy}
                prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                formatter={(v) => formatMoneda(v)}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos esta semana"
                value={stats.ingresosSemana}
                prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
                formatter={(v) => formatMoneda(v)}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos del mes"
                value={stats.ingresosMes}
                prefix={<DollarOutlined style={{ color: '#faad14' }} />}
                formatter={(v) => formatMoneda(v)}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Próximas reservas */}
        <Card
          title="Próximas reservas"
          extra={
            <Link to="/club/turnos">
              <Button type="link" size="small">
                Ver turnos
              </Button>
            </Link>
          }
          style={{ background: '#2d2d2d', borderColor: '#404040' }}
        >
          {proximasReservas.length === 0 ? (
            <Text type="secondary">No hay reservas próximas este mes.</Text>
          ) : (
            <Table
              columns={columnasProximas}
              dataSource={proximasReservas}
              rowKey={(r) => `${r.rcl_id}_${(r.tcl_fecha || '').toString().substring(0, 10)}`}
              pagination={false}
              size="small"
              scroll={isMobile ? { x: 400 } : undefined}
            />
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default DashboardClub;
